import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { Suspense, useEffect, useRef, useState } from 'react';
import { darkTheme, lightTheme } from './highchartsTheme';

const UnitSensorData = ({ unitData, isLoadingData, handlePatchSensor }) => {
  const chartComponentRef = useRef(null);
  const [isChartReady, setIsChartReady] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [measuredVolume, setMeasuredVolume] = useState(0);
  const [actualThroughput, setActualThroughput] = useState('');
  const [correctionCoefficient, setCorrectionCoefficient] = useState(1);
  const [timeRange, setTimeRange] = useState({
    startTime: null,
    endTime: null,
  });

  const theme = useTheme();

  const [chartOptions, setChartOptions] = useState({
    chart: {
      height: 550,
      style: { padding: 4 },
      zooming: { type: 'x', mouseWheel: { enabled: false } },
      navigator: { enabled: false },
      scrollbar: { enabled: false },
      // title: { text: alarm?.Title || 'Alarm Data' },
      events: {
        load: function () {
          setIsChartReady(true);
        },
        destroy: function () {
          setIsChartReady(false);
        },
        selection: function (event) {
          if (event.xAxis) {
            const startTime = event.xAxis[0].min; // Start timestamp
            const endTime = event.xAxis[0].max; // End timestamp

            // Ensure data exists and is valid
            if (!unitData || !unitData[0]) {
              console.warn('Unit data is missing or invalid.');
              return false; // Exit early
            }

            // Filter data points within the selected time range
            const filteredData = unitData[0].data
              ?.filter(
                ([timestamp]) => timestamp >= startTime && timestamp <= endTime,
              )
              ?.sort(([a], [b]) => a - b); // Sort by timestamp for safety

            // Check if filtered data is valid
            if (!filteredData || filteredData.length < 2) {
              console.warn(
                'Not enough data points in the selected range. Filtered data:',
                filteredData,
              );
              return false;
            }

            // Extract first and last values from the sorted filtered data
            const firstValue = filteredData[0][1];
            const lastValue = filteredData[filteredData.length - 1][1];

            // Ensure values are valid
            if (
              typeof firstValue !== 'number' ||
              typeof lastValue !== 'number'
            ) {
              console.warn('Invalid values for first or last value.');
              return false;
            }

            // Calculate total volume with precision
            const totalVolume = parseFloat(
              (lastValue - firstValue).toFixed(10),
            );

            // Update state
            setMeasuredVolume(totalVolume);
            setTimeRange({ startTime, endTime });
            setDialogOpen(true); // Open the dialog
          }

          return false; // Prevent the default behavior
        },
      },
    },
    time: { useUTC: true },
    legend: {
      enabled: false,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
    },
    rangeSelector: { enabled: false },
    yAxis: [
      {
        title: { text: 'Volume (m³)' },
        opposite: true,
        labels: { format: '{value} m³' },
        offset: 75,
      },
    ],
    xAxis: { type: 'datetime', plotLines: [] },
    scrollbar: { enabled: false },
    loading: {
      labelStyle: {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#666',
      },
      style: {
        backgroundColor: 'transparent',
        opacity: 0,
      },
      showDuration: 100, // Smoothly show
      hideDuration: 100, // Smoothly hide
    },
    series: [], // Default empty series
  });

  useEffect(() => {
    if (actualThroughput) {
      const newCoefficient = parseFloat(actualThroughput) / measuredVolume;
      setCorrectionCoefficient(newCoefficient);
    }
  }, [actualThroughput, measuredVolume]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setActualThroughput('');
    setCorrectionCoefficient(1);
  };

  const handleSave = () => {
    handlePatchSensor({ VolumeConstant: correctionCoefficient });
    handleDialogClose();
  };

  // Update chart options if data is available
  useEffect(() => {
    let timeoutId;
    if (isChartReady && unitData && unitData.length > 0) {
      timeoutId = setTimeout(() => {
        setChartOptions((prevOptions) => ({
          ...prevOptions,
          series: unitData,
          navigator: {
            enabled: false, // Keep disabled
          },
          xAxis: {
            ...prevOptions.xAxis,
          },
          tooltip: {
            split: false,
            followPointer: true,
            headerFormat: '',
            pointFormatter: function () {
              return `${this.series.name}: <b>${this.y} ${
                unitData[0].base
              }</b><br/>${Highcharts.dateFormat(
                '%A, %b %e, %Y %H:%M:%S',
                this.x,
              )}`;
            },
          },
        }));
      }, 500); // Adjust the timeout duration as needed
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [unitData]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    Highcharts.setOptions(themeOptions);

    if (
      isChartReady &&
      chartComponentRef.current &&
      chartComponentRef.current.chart
    ) {
      chartComponentRef.current.chart.update({
        chart: {
          backgroundColor: themeOptions.chart.backgroundColor,
        },
        title: {
          style: {
            color: themeOptions.title.style.color,
          },
        },
        xAxis: {
          gridLineColor: themeOptions.xAxis.gridLineColor,
          lineColor: themeOptions.xAxis.lineColor,
          labels: {
            style: {
              color: themeOptions.xAxis.labels.style.color,
            },
          },
        },
        yAxis: [
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
          {
            gridLineColor: themeOptions.yAxis.gridLineColor,
            labels: {
              style: {
                color: themeOptions.yAxis.labels.style.color,
              },
            },
          },
        ],
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
        },
      });
    }
  }, [theme]);

  // Handle loading state using chart ref
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoadingData) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoadingData]);

  return (
    <>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '450px',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={'stockChart'}
          options={chartOptions}
          ref={chartComponentRef}
        />
      </Suspense>

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Measured Volume</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {new Date(timeRange.startTime).toLocaleString()} to{' '}
            {new Date(timeRange.endTime).toLocaleString()}
          </Typography>
          <TextField
            label="Actual Throughput (m³)"
            variant="filled"
            fullWidth
            margin="normal"
            type="number"
            value={actualThroughput}
            helperText="Enter the actual throughput value measured in cubic meters (m³)"
            onChange={(e) => setActualThroughput(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1 } }}
          />
          {/*           
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                label="Measured Throughput (m³)"
                variant="filled"
                fullWidth
                margin="normal"
                type="number"
                readOnly
                disabled
                value={measuredVolume}
                InputProps={{ disableUnderline: true, sx: { borderRadius: 1 } }}
              />
            </Grid>

            <Grid item xs={4}>
              <ListItemText 
                primary="Volume Constant"
                primaryTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
                secondary={<b>{correctionCoefficient.toFixed(2)}</b>}
                secondaryTypographyProps={{ variant: 'body1', color: 'textPrimary', fontSize: '1.5rem' }}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>  */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnitSensorData;
